import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/money';

import type { PromotionAnalyticsSummary } from '../types';

interface Props {
  summary?: PromotionAnalyticsSummary;
  currency: string;
  isLoading: boolean;
}

function KpiCard({ label, value, isLoading }: { label: string; value: string; isLoading: boolean }) {
  return (
    <div className="bg-card flex flex-col gap-1.5 rounded-lg border p-4 shadow-sm">
      <span className="text-muted-foreground text-sm font-medium">{label}</span>
      {isLoading ? (
        <Skeleton className="h-8 w-28" />
      ) : (
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
      )}
    </div>
  );
}

export function PromotionAnalyticsKpis({ summary, currency, isLoading }: Props) {
  const { t } = useTranslation('analytics');
  const money = (v: number) => (currency ? formatMoney(v, currency) : '—');

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label={t('promotionAnalytics.kpis.redemptions')}
        value={(summary?.redemptions ?? 0).toLocaleString()}
        isLoading={isLoading}
      />
      <KpiCard
        label={t('promotionAnalytics.kpis.discountedSales')}
        value={money(summary?.discountedSales ?? 0)}
        isLoading={isLoading}
      />
      <KpiCard
        label={t('promotionAnalytics.kpis.netSales')}
        value={money(summary?.netSales ?? 0)}
        isLoading={isLoading}
      />
      <KpiCard
        label={t('promotionAnalytics.kpis.discountGiven')}
        value={money(summary?.discountGiven ?? 0)}
        isLoading={isLoading}
      />
    </div>
  );
}
